import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StepIndicator } from '../components/StepIndicator';
import { OnboardingData, initialOnboardingData } from '../types/onboarding';
import { UserDetails } from '../components/steps/UserDetails';
import { StatesLicensed } from '../components/steps/StatesLicensed';
import { ReviewCreate } from '../components/steps/ReviewCreate';
import { OffboardingPanel } from '../components/OffboardingPanel';
import { Logo } from '../components/Logo';
const STEPS = [{
  id: 1,
  title: 'User Details',
  description: 'Enter the core information for the new Salesforce user and verify license availability.'
}, {
  id: 2,
  title: 'States Licensed',
  description: 'Select the states where the user is licensed to operate.'
}, {
  id: 3,
  title: 'Review & Create',
  description: 'Review all configurations before creating the user and associated records.'
}];
const OFFBOARDING_STEPS = [{
  id: 1,
  title: 'Select User',
  description: 'Identify the user being terminated.'
}, {
  id: 2,
  title: 'Review Config',
  description: 'Load and review offboarding metadata configuration.'
}, {
  id: 3,
  title: 'Reassignment',
  description: 'Reassign subordinates if applicable.'
}, {
  id: 4,
  title: 'Deactivation',
  description: 'Configure and review deactivation actions.'
}, {
  id: 5,
  title: 'Execute',
  description: 'Execute offboarding and verify completion.'
}];
interface OnboardingWizardProps {
  mode: 'onboarding' | 'offboarding';
  setMode: (mode: 'onboarding' | 'offboarding') => void;
}
export function OnboardingWizard({
  mode,
  setMode
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [offboardingStep, setOffboardingStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingData>(initialOnboardingData);
  const [canAdvance, setCanAdvance] = useState(false);
  const updateFormData = (updates: Partial<OnboardingData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canAdvance) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleStepClick = (index: number) => {
    if (index < currentStep) {
      setCurrentStep(index);
    }
  };
  const isLastStep = currentStep === STEPS.length - 1;
  const renderStepContent = () => {
    const props = {
      data: formData,
      updateData: updateFormData,
      setCanAdvance
    };
    switch (currentStep) {
      case 0:
        return <UserDetails {...props} data-id="element-518" data-mp="1z7a9tgo82ob1" />;
      case 1:
        return <StatesLicensed {...props} data-id="element-519" data-mp="1u6w25zqj9gdj" />;
      case 2:
        return <ReviewCreate {...props} onBack={handleBack} data-id="element-520" data-mp="1u8fddbxc118g" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen w-full bg-[#F0F2F5] font-sans text-gray-900 pb-12" data-id="element-521" data-mp="1p9m27q5ipmbg">
      {/* Top Header Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6" data-id="element-522" data-mp="9o1zilzqotvp">
        <div className="flex items-center justify-between" data-id="element-523" data-mp="9q4xptb1ptbi">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex-shrink-0" data-id="element-524" data-mp="eo6e26h37eja">
            <Logo data-id="element-525" data-mp="3r51wj9wpzg60" />
          </motion.div>

          {/* License Counters */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="flex items-center gap-3" data-id="element-526" data-mp="3r2ywry5rm8o3">
            {/* Salesforce */}
            <div className="bg-white rounded-xl px-4 py-2.5 shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-527" data-mp="2kavpxmdze1e8">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-navy-600 flex-shrink-0" fill="currentColor" data-id="element-528" data-mp="2f9okhtzb9eaq">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" data-id="element-529" data-mp="2p848ho31k7g0" />
              </svg>
              <div className="flex flex-col items-end leading-none" data-id="element-530" data-mp="380gdkil2xmtu">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-531" data-mp="37y3zigifzwm2">
                  Salesforce
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-532" data-mp="1dmxfn7n50280">
                  14<span className="text-gray-400 font-medium" data-id="element-533" data-mp="3p9rb13lxv3xt">/58</span>
                </span>
              </div>
            </div>

            {/* Pulse */}
            <div className="bg-white rounded-xl px-4 py-2.5 shadow-sm border border-gray-100 flex items-center gap-3" data-id="element-534" data-mp="3u7ssjk3puv76">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-535" data-mp="3fhvazn0al3qy">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" data-id="element-536" data-mp="2wkttx3tgrd6n" />
              </svg>
              <div className="flex flex-col items-end leading-none" data-id="element-537" data-mp="2wmoh6os7jhdw">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider" data-id="element-538" data-mp="31kpwqzqfcykm">
                  Pulse
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-539" data-mp="urnutyy2kdjh">
                  6<span className="text-gray-400 font-medium" data-id="element-540" data-mp="zs1t4p9vc94b">/52</span>
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-541" data-mp="tb0avd98qp75">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row min-h-[600px] overflow-hidden" data-id="element-542" data-mp="1h3yumzyetnlr">
          {/* Left Column: Navigation */}
          <div className="w-full md:w-[240px] border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0 bg-white" data-id="element-543" data-mp="17ai17x26qaim">
            <StepIndicator steps={mode === 'offboarding' ? OFFBOARDING_STEPS : STEPS} currentStep={mode === 'offboarding' ? offboardingStep : currentStep} onStepClick={handleStepClick} mode={mode} setMode={setMode} data-id="element-544" data-mp="12ax13jba3w4b" />
          </div>

          {/* Right Column: Form Area */}
          <div className="flex-1 flex flex-col bg-white relative" data-id="element-545" data-mp="27c6cyx6vvopn">
            {mode === 'offboarding' ? <OffboardingPanel onStepChange={setOffboardingStep} data-id="element-546" data-mp="2cd2hncz27ew4" /> : <div className="flex-1 p-6 md:p-10 overflow-y-auto" data-id="element-547" data-mp="2hbdqbkdmbjcr">
                {/* Step Header */}
                <div className="mb-8" data-id="element-548" data-mp="2m8x6wqxsg87v">
                  <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-549" data-mp="2qailqhtvuwkp">
                    {STEPS[currentStep].title}
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-550" data-mp="2v20qlgz11hay">
                    {STEPS[currentStep].description}
                  </p>
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait" data-id="element-551" data-mp="350y9o4dooimk">
                  <motion.div key={currentStep} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} data-id="element-552" data-mp="39yrf2v75x8ls">
                    {renderStepContent()}
                  </motion.div>
                </AnimatePresence>

                {/* Inline Navigation (Hidden on last step as it handles its own submit) */}
                {!isLastStep && <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-553" data-mp="3gksd4zqobm6x">
                    {currentStep > 0 ? <button onClick={handleBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-554" data-mp="2st4tn2vqrdeo">
                          Previous
                        </button> : <div data-id="element-555" data-mp="udnvzrwk2atk"></div> // Spacer
              }

                    <button onClick={handleNext} disabled={!canAdvance} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-colors duration-150 ease-out shadow-sm ${!canAdvance ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-navy-600 text-white hover:bg-navy-700'}`} data-id="element-556" data-mp="zcieesqm1xd5">
                      Next
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
}